import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import Radom, {
  CheckoutSession, IDiscountCode, IManagedPaymentMethodDetails, IManagedPaymentStatus,
  IManagedTokenDelegatePaymentMethodDetails, PaymentMethod, ProductWithQuantity, Profile
} from '../api/Radom'
import { CheckoutData, zeroTravelRuleFieldsCollected } from '../components/Checkout'
import PayWidget, { type CheckoutParams } from '../components/PayWidget'
import Spinner from '../components/Spinner'
import { Chevron } from '../icons/Chevron'
import { getTotalSum } from '../util/Products'
import { RADOM_COLORS, formatSubscriptionDuration } from '../util/Constants'
import { CheckmarkWrapper } from '../components/Receipt'
import Checkmark from '../icons/Checkmark'
import { pickTextColorBasedOnBgColor, shortAddress, isValidPhone } from '../util/Util'
import RadomLogo from '../icons/Logo2'
import { IconButton, SecondaryButton, PrimaryButton } from '../components/Button'
import { formatCurrency } from '../util/Currencies'
import { InputLabel, TextInput, TextInputWithLabel, TextInputWithLabelWithSuggestions } from '../components/Input'
import Close from '../icons/Close'
import { IvyCheckout } from '@getivy/react-sdk'
import '@getivy/react-sdk/dist/index.css'
import { t, tBank, setLanguage, Language } from '../util/i18n'
import { Country, ICountry } from 'country-state-city'
import DateSelector from '../components/DateSelector'
import KycView from './KycView'
import _ from 'lodash'
import Arrow from '../icons/Arrow'
import Crossmark from '../icons/Crossmark'
import PendingSpinner from '../icons/PendingSpinner'
import CountrySelector from '../components/CountrySelector'
import { subYears } from 'date-fns'
import { ManagedPaymentMethod } from '../util/Managed'
import { OnrampComponent } from '../components/OpenBanking'
import { LinkArrow } from '../icons/LinkArrow'
import USDCLogo from '../icons/USDCLogo'
import PaymentMethodDisplay from '../components/PaymentMethodDisplay'
import Edit from '../icons/Edit'

const CURRENCY_USD = 'USD'
const DEFAULT_FIAT_CURRENCY = CURRENCY_USD

const PayWrapper = styled.div`
  width: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: auto;
  margin-bottom: auto;
  padding-top: 50px;

  @media (max-width: 768px) {
    width: 100%;
  }
`

const Container = styled.div`
  width: 100vw;
  height: 100%;
  height: 100vh;
  background-color: white;
  font-size: 14px;

  @media (max-width: 800px) {
    min-height: 100vh;
    height: auto;
  }
`

const LeftSection = styled.div<{ leftPanelColor: string, slantedEdge?: boolean }>`
  color: ${({ leftPanelColor }) =>
    pickTextColorBasedOnBgColor(leftPanelColor, 'white', 'black')};
  background: ${({ leftPanelColor }) => leftPanelColor};
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  min-width: min-content;
  width: 45%;
  font-size: 24px;

  @media (max-width: 800px) {
    width: 100%;
    clip-path: none;
    padding-right: 0px;
  }
`

const LeftSectionInner = styled.div<{ isSumPurchase?: boolean }>`
  padding: 40px 80px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: ${({ isSumPurchase }) => isSumPurchase ? 'center' : 'start'};
  align-items: ${({ isSumPurchase }) => isSumPurchase ? 'initial' : 'start'};
  width: ${({ isSumPurchase }) => isSumPurchase ? 'initial' : '100%'};
  gap: 40px;

  @media (max-width: 800px) {
    width: 100%;
    padding: 40px;
  }
`

export const RightSection = styled.div`
  background-color: white;
  display: flex;
  align-items: center;
  margin: auto;
  flex-direction: column;
  justify-content: space-between;
  gap: 20px;
  flex-grow: 1;
  min-height: 100vh;

  @media (max-width: 800px) {
    padding: 40px;
    width: 100%;
    height: min-content;
    justify-content: initial;
  }
`

interface kycData {
  name: string
  phoneNumber: string
  countryOfResidence: string
  // city: string
  address: string
  // postalCode: string
  dateOfBirth: string
  //
  passportFile?: string
  idCardFile?: string
  selfieFile?: string
}

enum OpenBankingRenderStates {
  onramp,
  billing,
  summary
}

enum PaymentStatus {
  INITIAL = 'initial',
  KYC = 'kyc',
  KYC_PENDING_VIEW = 'kyc_pending_view',
  WAITING = 'waiting',
  SUCCESS = 'success',
  Expired = 'expired',
  ERROR = 'error'
}

interface RenderOpenBankingInitialProps {
  renderState: OpenBankingRenderStates
  setRenderState: React.Dispatch<React.SetStateAction<OpenBankingRenderStates>>
  checkoutData: CheckoutData | undefined
  totalSum: number
  currency: string
  isPreProcessingPayment: boolean
  onrampAmount: number
  setOpenBankingTotalUsdRate: React.Dispatch<React.SetStateAction<number>>
  openBankingCheckoutUrl: string
  setOpenBankingCheckoutUrl: React.Dispatch<React.SetStateAction<string>>
  randomAddress: string
  userKycData: kycData
  setUserKycData: React.Dispatch<React.SetStateAction<kycData>>
  addressSuggestionList: string[]
  setAddressSuggestionList: React.Dispatch<React.SetStateAction<string[]>>
  allCountries: ICountry[]
  debouncedFetch: (value: string, isoCode: string, setter: (res: string[]) => void) => void
  checkMarkOpenbanking: boolean
  setCheckMarkOpenBanking: (val: boolean) => void
  errorMessage: string
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>
  setOpenbankPayStatus: React.Dispatch<React.SetStateAction<PaymentStatus>>
}

const RenderOpenBankingInitial = ({
  renderState,
  setRenderState,
  checkoutData,
  totalSum,
  currency,
  isPreProcessingPayment,
  onrampAmount,
  randomAddress,
  userKycData,
  setUserKycData,
  addressSuggestionList,
  setAddressSuggestionList,
  allCountries,
  debouncedFetch,
  checkMarkOpenbanking,
  setCheckMarkOpenBanking,
  errorMessage,
  setErrorMessage,
  setOpenbankPayStatus,
  setOpenBankingCheckoutUrl
}: RenderOpenBankingInitialProps): ReactElement => {
  const { checkoutId } = useParams()
  const fromMethod: ManagedPaymentMethod = useMemo(() => {
    if (checkoutData?.currency === 'EUR') {
      return {
        name: 'EUR',
        ticker: 'EUR',
        network: 'Fiat',
        token: 'EUR',
        currencyName: 'Euro',
        isWalletSupported: false
      }
    } else {
      return {
        name: 'GBP',
        ticker: 'GBP',
        network: 'Fiat',
        token: 'GBP',
        currencyName: 'British Pound',
        isWalletSupported: false
      }
    }
  }, [checkoutData?.gateway.openBanking])
  const toMethod: ManagedPaymentMethod = useMemo(() => {
    return {
      name: 'USDC',
      ticker: 'USDC',
      network: 'Ethereum',
      currencyName: 'USDC',
      isWalletSupported: false
    }
  }, [checkoutData?.gateway.openBanking])

  const onBack = (): void => {
    if (renderState === OpenBankingRenderStates.billing) {
      setRenderState(OpenBankingRenderStates.onramp)
    } else if (renderState === OpenBankingRenderStates.summary) {
      setRenderState(OpenBankingRenderStates.billing)
    }
  }

  const onChangeFullName = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setUserKycData(prev => ({ ...prev, name: e.target.value }))
  }, [])

  const [isLoading, setIsLoading] = useState(false)

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            justifyItems: 'center',
            alignItems: 'center',
            marginTop: 10,
            marginRight: 5,
            fontSize: 20,
            fontWeight: 600
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {
              renderState !== OpenBankingRenderStates.onramp && (
                <Chevron style={{
                  transform: 'rotate(90deg)',
                  cursor: 'pointer',
                  userSelect: 'none',
                  color: RADOM_COLORS.GRAY_DARKEST
                }}
                onClick={onBack} width={14} height={14} color={RADOM_COLORS.GRAY_DARKEST}
                />
              )
            }
            {
              renderState === OpenBankingRenderStates.onramp
                ? <div>{t('amountToBePaid')}</div>
                : renderState === OpenBankingRenderStates.billing
                  ? <div>{t('billingInformation')}</div>
                  : <div>{t('checkoutSummary')}</div>
            }
          </div>

          {
            renderState === OpenBankingRenderStates.onramp && (
              <div>{formatCurrency(currency, totalSum)}</div>
            )
          }
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            justifyItems: 'center',
            alignItems: 'center',
            marginTop: 4,
            marginRight: 5,
            fontSize: 14,
            color: 'white',
            fontWeight: 400
          }}>
          {
            renderState === OpenBankingRenderStates.onramp && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                alignItems: 'center',
                color: RADOM_COLORS.GRAY_DARKEST,
                width: '100%',
                padding: '0'
              }}>
                <OnrampComponent
                  style={{ marginTop: 35, marginBottom: 2.5 }}
                  method={fromMethod}
                  tag={t('youSpend')}
                  amount={totalSum.toString()}
                  loading={isPreProcessingPayment}
                />
                <div style={{
                  margin: '-26px',
                  width: 40,
                  height: 40,
                  background: 'white',
                  zIndex: 1,
                  borderRadius: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <LinkArrow style={{ transform: 'rotate(90deg)', width: 24 }} stroke={RADOM_COLORS.GRAY_DARKER} />
                </div>
                <OnrampComponent
                  style={{ marginTop: 2.5 }}
                  method={toMethod}
                  tag={t('youReceive')}
                  amount={parseFloat(onrampAmount.toString()).toFixed(2)}
                  loading={isPreProcessingPayment}
                />
                <div
                  style={{
                    marginTop: 10,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10
                  }}>
                  <div style={{
                    color: RADOM_COLORS.BLACK,
                    marginLeft: 10,
                    marginBottom: 5,
                    fontSize: 14,
                    fontWeight: 500
                  }}>
                    {t('paymentMethod')}
                  </div>
                  <div
                    style={{
                      padding: 20,
                      backgroundColor: RADOM_COLORS.GRAY_LIGHTEST,
                      borderRadius: 20,
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                    <div>
                      {t('instantBankTransfer')}
                    </div>
                    <img src={tBank()} style={{ height: 20, width: 'auto' }} />
                  </div>
                </div>
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10
                  }}>
                  <div style={{
                    color: RADOM_COLORS.BLACK,
                    marginLeft: 10,
                    marginBottom: 5,
                    fontSize: 14,
                    fontWeight: 500
                  }}>
                    {t('receivingAddress')}
                  </div>
                  <div
                    style={{
                      padding: 20,
                      backgroundColor: RADOM_COLORS.GRAY_LIGHTEST,
                      borderRadius: 20,
                      width: '100%',
                      display: 'flex',
                      overflowX: 'auto',
                      scrollbarWidth: 'none'
                    }}>
                    <div>
                      {checkoutData?.gateway.openBanking?.destinationCryptoAddress ?? randomAddress}
                    </div>
                  </div>
                </div>
                <PrimaryButton
                  style={{ width: '100%', marginTop: 20 }}
                  onClick={() => setRenderState(OpenBankingRenderStates.billing)}
                >
                  {t('continueToCheckout')}
                </PrimaryButton>
              </div>

            )
          }

          {
            renderState === OpenBankingRenderStates.billing && (
              <div style={{
                height: '100%',
                minHeight: '475px',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                color: RADOM_COLORS.BLACK,
                fontSize: 14,
                marginTop: 10
              }}>
                <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', justifyContent: 'space-between', width: '100%' }}>
                  <div style={{ fontSize: 14, display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
                    <TextInputWithLabel
                      label={t('fullName')}
                      value={userKycData.name}
                      required={true}
                      onChange={onChangeFullName}
                    />
                  </div>

                  <div style={{ fontSize: 14, display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
                    <InputLabel>
                      <span>{t('dateOfBirth')}</span>
                    </InputLabel>
                    <DateSelector
                      placeholder='DD/MM/YYYY'
                      value={userKycData.dateOfBirth ? new Date(userKycData.dateOfBirth) : undefined}
                      onChange={(e) => setUserKycData({ ...userKycData, dateOfBirth: e?.toDateString() ?? '' })}
                      startDate={new Date('1935-01-01')}
                      endDate={subYears(new Date(), 18)}
                    />
                  </div>
                </div>
                <TextInputWithLabel
                  label={t('email')}
                  disabled={true}
                  value={checkoutData?.gateway.openBanking?.email ?? ''}
                  required={true}
                  onChange={() => { }}
                />

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
                  <InputLabel>{t('countryoOfResidence')}</InputLabel>
                  <CountrySelector
                    allowedCountries={allCountries}
                    selectedCountry={userKycData.countryOfResidence || ''}
                    onCountrySelected={country => {
                      if (country) {
                        setUserKycData({
                          ...userKycData,
                          phoneNumber: `+${country.phonecode} `,
                          countryOfResidence: country.name
                        })
                      }
                    }}
                  />
                </div>
                {/* <input className="pac-container" /> */}
                {
                  userKycData.countryOfResidence !== '' &&
                  <>
                    <TextInputWithLabelWithSuggestions
                      label={t('address')}
                      value={userKycData.address}
                      required={true}
                      disabled={userKycData.countryOfResidence === ''}
                      suggestionList={addressSuggestionList}
                      inputStyle={{
                        cursor: userKycData.countryOfResidence === '' ? 'not-allowed' : 'text'
                      }}
                      setSelectedItems={(items) => {
                        setUserKycData({
                          ...userKycData,
                          address: items[0]
                        })
                        setAddressSuggestionList([])
                      }
                      }
                      onChange={(e) => {
                        const value = (e.target as HTMLInputElement).value
                        if (value.length === 0) {
                          setAddressSuggestionList([])
                        }

                        setUserKycData({
                          ...userKycData,
                          address: value
                        })

                        if (value.length > 5 && userKycData.countryOfResidence) {
                          const country = allCountries.find(
                            (c) => c.name === userKycData.countryOfResidence
                          )
                          if (country) {
                            debouncedFetch(value, country.isoCode, setAddressSuggestionList)
                          }
                        }
                      }}
                    />

                    <TextInputWithLabel
                      label={t('phoneNumber')}
                      required
                      value={userKycData.phoneNumber}
                      onChange={(e) =>
                        setUserKycData({ ...userKycData, phoneNumber: (e.target as HTMLInputElement).value })
                      }
                    />
                  </>

                }

                <div style={{ position: 'sticky', bottom: 0, background: 'white', paddingTop: 20, zIndex: 2 }}>
                  <PrimaryButton
                    disabled={!userKycData.name ||
                        !userKycData.dateOfBirth ||
                        !userKycData.countryOfResidence ||
                        !userKycData.address}
                    style={{ width: '100%', marginTop: 50 }}
                    onClick={() => {
                      if (!isValidPhone(userKycData.phoneNumber)) {
                        setErrorMessage('Please enter a valid phone number')
                        return
                      }
                      setErrorMessage('')
                      setRenderState(OpenBankingRenderStates.summary)
                    }}
                  >
                    {t('continueToSummary')}
                  </PrimaryButton>
                </div>
              </div>
            )
          }

          {
            renderState === OpenBankingRenderStates.summary && (
              <div style={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 24,
                color: RADOM_COLORS.BLACK,
                fontSize: 14,
                marginTop: 10
              }}>

                <div
                  style={{
                    width: '100%',
                    padding: 24,
                    borderRadius: 20,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    gap: 16
                  }}
                >

                  <div style={{ fontSize: 13, color: '#6B6C7E' }}>
                    {"You're paying"}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      gap: 8
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                      <PaymentMethodDisplay
                        size={28}
                        method={fromMethod}
                        excludeName
                      />
                      <span style={{ fontSize: 34, fontWeight: 600 }}>
                        {Number(totalSum).toFixed(2)} {currency}
                      </span>
                    </div>
                  </div>
                  <div style={{ fontSize: 13, color: '#6B6C7E' }}>
                    Including all fees
                  </div>
                  <div
                    style={{
                      height: 1.5,
                      backgroundColor: RADOM_COLORS.GRAY_DARKEST,
                      width: '100%',
                      opacity: 0.1
                    }}
                  ></div>
                </div>

                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                      width: '100%'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        // lineHeight: '28px',
                        marginBottom: 14
                      }}
                    >
                      <div style={{ fontSize: 16, color: '#6B6C7E', fontWeight: 500 }}>
                        {t('estTime')}
                      </div>
                      <div style={{ fontWeight: 500, fontSize: 14 }}>
                        {t('INSTANT')}
                      </div>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start'
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12,
                          color: '#6B6C7E',
                          lineHeight: '20px'
                        }}
                      >
                        {t('youReceive')}
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                          gap: 4
                        }}
                      >
                        <div style={{ lineHeight: '18px', fontWeight: 200 }}>
                          {Number(onrampAmount).toFixed(2)}
                          <span
                            style={{ fontSize: 10, color: RADOM_COLORS.GRAY_DARKEST }}> USDC </span>
                        </div>

                        <div style={{ fontSize: 8, color: '#6B6C7E' }}>
      on Ethereum
                        </div>
                      </div>
                    </div>

                  </div>

                  <div
                    style={{
                      padding: 16,
                      backgroundColor: RADOM_COLORS.GRAY_LIGHTEST,
                      borderRadius: 16,
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 12,
                      flexDirection: 'column',
                      overflowX: 'auto',
                      scrollbarWidth: 'none',
                      marginTop: 10,
                      opacity: 0.6
                    }}
                  >
                    <div
                      style={{
                        color: RADOM_COLORS.GRAY_DARKEST,
                        fontSize: 14
                      }}
                    >
                      {t('receivingAddress')}
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                      <div style={{ fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all' }}>
                        {checkoutData?.gateway.openBanking?.destinationCryptoAddress ?? randomAddress}
                      </div>
                      <Edit
                        stroke={RADOM_COLORS.GRAY_DARKER}
                        style={{ height: 16, width: 'auto', cursor: 'pointer' }}
                        onClick={() => window.open('https://radom.com', '_blank')}
                      />
                    </div>

                  </div>
                </div>

                <div style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                  marginTop: 40
                }}>
                  <div
                    style={{
                      marginLeft: 5,
                      fontSize: 11,
                      lineHeight: 1.5,
                      color: RADOM_COLORS.GRAY_DARKEST
                    }}
                  >
                    By continuing, you agree to our{' '}
                    <span
                      style={{ color: RADOM_COLORS.NEW_BLUE, cursor: 'pointer' }}
                      onClick={() => window.open('https://www.radom.com/terms', '_blank')}
                    >
                      Terms and Conditions
                    </span>{' '}
                    and{' '}
                    <span
                      style={{ color: RADOM_COLORS.NEW_BLUE, cursor: 'pointer' }}
                      onClick={() => window.open('https://www.radom.com/acceptable-use-policy', '_blank')}
                    >
                      Acceptable Use Policy
                    </span>
                    . You confirm that you are not purchasing crypto assets for investment purposes and understand
                    the risks involved in digital asset transactions.
                  </div>
                  <>
                    <style>{`
    @keyframes monoFlow {
      0% {
        background-position: 0% 50%;
      }
      50% {
        background-position: 100% 50%;
      }
      100% {
        background-position: 0% 50%;
      }
    }
  `}</style>
                    <button
                      disabled={isLoading}
                      onClick={() => {
                        setIsLoading(true)
                        localStorage.setItem('OpenBankingAprove', 'true')

                        if (!checkoutData?.gateway.openBanking?.openBankingCheckoutUrl.includes('https')) {
                          Radom.updateCustomerKycData(
                            checkoutId ?? '',
                            {
                              name: userKycData.name,
                              phone: userKycData.phoneNumber,
                              address: userKycData.address,
                              dateOfBirth: new Date(userKycData.dateOfBirth).toLocaleDateString('en-CA'),
                              residenceCountry: userKycData.countryOfResidence
                            }
                          )
                            .then(async () => {
                              const res = await Radom.getCheckoutSession(checkoutId ?? '')
                              const checkoutUrl = res.gateway.openBanking?.openBankingCheckoutUrl
                              if (!checkoutUrl) {
                                setOpenbankPayStatus(PaymentStatus.KYC)
                              } else {
                                setOpenBankingCheckoutUrl(checkoutUrl)
                                setOpenbankPayStatus(PaymentStatus.WAITING)
                              }
                              setIsLoading(false)
                            })
                            .catch((e) => {
                              setErrorMessage(e.message || '')
                              setIsLoading(false)
                            })
                        } else {
                          setIsLoading(false)
                          setOpenbankPayStatus(PaymentStatus.WAITING)
                        }
                      }}
                      style={{
                        marginTop: 20,
                        width: '100%',
                        height: 80,
                        borderRadius: 30,
                        fontSize: 20,
                        border: 0,
                        color: 'white',
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 10,
                        position: 'relative',

                        background: `
                        linear-gradient(
  120deg,
  #3464E6,
  #618afa,
  #3464E6
)
                              `,
                        backgroundSize: '300% 300%',
                        animation: isLoading
                          ? 'none'
                          : 'monoFlow 6s ease-in-out infinite, monoGlowRing 1.8s ease-out infinite',

                        opacity: isLoading ? 0.6 : 1,
                        transition: 'opacity 0.2s ease'
                      }}
                    >
                      {isLoading
                        ? (
                          <Spinner />
                        )
                        : (
                          <>
                            <span>{t('agreeAndContinue')}</span>
                          </>
                        )}
                    </button>
                  </>
                </div>
              </div>
            )
          }

        </div>
      </div>

      {
        errorMessage && errorMessage.length > 0 &&
          <div style={{
            backgroundColor: RADOM_COLORS.ERROR,
            padding: 15,
            borderRadius: 5,
            marginBottom: 20,
            color: 'white',
            overflowWrap: 'break-word'
          }}>
            {errorMessage}
          </div>
      }

      <div style={{ display: 'flex', justifyContent: 'center', width: '100%', fontSize: 12 }}>

        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>

          <a
            href='https://www.radom.com/industries/igaming'
            target='_blank'
            title='Crypto Presale Powered by Radom'
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              cursor: 'pointer',
              userSelect: 'none',
              textDecoration: 'none',
              color: RADOM_COLORS.GRAY_DARKEST
            }} rel="noreferrer"
          >
            <span style={{ display: 'flex', alignItems: 'center', fontSize: 14, fontWeight: 100 }}>
                Powered by
              <RadomLogo
                width={60}
                height={14}
                fill={RADOM_COLORS.GRAY_DARKEST}
              />
            </span>
            <span style={{ position: 'absolute', width: '1px', height: '1px', margin: '-1px', padding: 0, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', border: 0 }}>
                Radom
            </span>
          </a>
        </div>
      </div>
    </>

  )
}

const HostedCheckout = (): React.ReactElement => {
  const { checkoutId } = useParams()
  const [searchParams] = useSearchParams()

  const posMode = useMemo(() => searchParams.get('posMode'), [])

  const [isLoading, setIsLoading] = useState(true)
  const [isPreProcessingPayment, setIsPreProcessingPayment] = useState(false)

  const [isExpired, setIsExpired] = useState(false)
  const [isCancelled, setIsCancelled] = useState(false)
  const [isComplete, setIsComplete] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [successElement, setSuccessElement] = useState<ReactElement>(<></>)
  const [errorMessage, setErrorMessage] = useState('')
  const [isSumPurchase, setIsSumPurchase] = useState(false)
  const [totalSum, setTotalSum] = useState(0)
  const [subscriptionSum, setSubscriptionSum] = useState(0)
  const [currency, setCurrency] = useState(DEFAULT_FIAT_CURRENCY)

  const [checkoutData, setCheckoutData] = useState<CheckoutData>()
  const [sellerProfile, setSellerProfile] = useState<Profile>()
  const [lines, setLines] = useState<ProductWithQuantity[]>([])
  const [textColor, setTextColor] = useState('red')
  const [addressSuggestionList, setAddressSuggestionList] = useState<string[]>([])

  const [managedPaymentDetails, setManagedPaymentDetails] = useState<IManagedPaymentMethodDetails>()
  const [
    tokenDelegatePaymentDetails,
    setTokenDelegatePaymentDetails
  ] = useState<IManagedTokenDelegatePaymentMethodDetails>()

  const [showDiscountCodeInput, setShowDiscountCodeInput] = useState(false)
  const [discountCode, setDiscountCode] = useState('')
  const [discountResult, setDiscountResult] = useState({ success: false, element: <></> })
  const [discount, setDiscount] = useState<IDiscountCode>()
  const [discountLocked, setDiscountLocked] = useState(false)

  const [paymentStatus, setPaymentStatus] = useState<IManagedPaymentStatus>()

  const [customizations, setCustomizations] = useState({
    leftPanelColor: RADOM_COLORS.GRAY_MED,
    primaryButtonColor: RADOM_COLORS.ORANGE,
    slantedEdge: false,
    allowDiscountCodes: false,
    displayQrCode: true
  })

  const [isOpenbank, setIsOpenbank] = useState(false)

  const [openbankPayStatus, setOpenbankPayStatus] = React.useState(PaymentStatus.INITIAL)
  const openBankingIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const allowCountries = [
    'Austria',
    'Belgium',
    'Bulgaria',
    'Croatia',
    'Cyprus',
    'Czech Republic',
    'Denmark',
    'Estonia',
    'Finland',
    'France',
    'Germany',
    'Greece',
    'Hungary',
    'Iceland',
    'Ireland',
    'Italy',
    'Latvia',
    'Liechtenstein',
    'Lithuania',
    'Luxembourg',
    'Malta',
    'Netherlands',
    'Norway',
    'Poland',
    'Portugal',
    'Romania',
    'Slovakia',
    'Slovenia',
    'Spain',
    'Sweden',
    'United Kingdom'
  ]
  const allCountries = Country.getAllCountries().filter((c) => {
    return allowCountries.includes(c.name)
  })

  const [userKycData, setUserKycData] = useState<kycData>({
    name: '',
    phoneNumber: '',
    countryOfResidence: '',
    address: '',
    dateOfBirth: '',
    passportFile: '',
    idCardFile: '',
    selfieFile: ''
  })

  const [checkMarkOpenbanking, setCheckMarkOpenBanking] = useState(false)

  const randomAddress = useMemo(() =>
    '0x' + [...Array(40)].map(() => '0123456789abcdef'[Math.floor(Math.random() * 16)]).join(''), []
  )

  const [openBankingCheckoutUrl, setOpenBankingCheckoutUrl] = useState('')
  const debouncedFetch = useRef(
    _.debounce((value: string, countryCode: string, setAddressSuggestionList) => {
      Radom.getGoolgePlaceAutoComplete(value, countryCode).then((res) => {
        setAddressSuggestionList(res)
      })
    }, 500)
  ).current

  // Start by assuming that none of the travel rule fields have been collected
  const [travelRuleFieldsCollected, setTravelRuleFieldsCollected] = useState(zeroTravelRuleFieldsCollected())

  useEffect(() => {
    if (!checkoutData?.gateway.openBanking) {
      return
    }

    if (openBankingIntervalRef.current) {
      clearInterval(openBankingIntervalRef.current)
    }

    if (checkoutData?.sessionStatus === 'success' || checkoutData?.sessionStatus === 'refunded' || checkoutData?.gateway.openBanking.instantPaymentStatus) {
      setOpenbankPayStatus(PaymentStatus.SUCCESS)
      return
    }

    if (checkoutData?.sessionStatus === 'expired') {
      setOpenbankPayStatus(PaymentStatus.Expired)
      return
    }

    if (checkoutData?.gateway.openBanking?.openBankingCheckoutUrl && checkoutData?.gateway.openBanking?.openBankingCheckoutUrl !== '') {
      if (checkoutData?.gateway.openBanking?.openBankingCheckoutUrl.includes('https://')) {
        setOpenbankPayStatus(PaymentStatus.KYC_PENDING_VIEW)
        const openBankingClicked = localStorage.getItem('OpenBankingAprove')
        setTimeout(() => {
          if (checkoutData?.gateway.openBanking?.existingUserPaidSuccess === true || openBankingClicked) {
            setOpenbankPayStatus(PaymentStatus.WAITING)
          } else {
            setOpenbankPayStatus(PaymentStatus.INITIAL)
          }
          setRenderOpenBankingState(OpenBankingRenderStates.summary)
        }, 2000)
      } else {
        setErrorMessage(checkoutData?.gateway.openBanking?.openBankingCheckoutUrl)
        setOpenbankPayStatus(PaymentStatus.ERROR)
      }
    }

    setIsLoading(false)
  }, [checkoutData?.gateway.openBanking])

  const getCheckoutData = async (): Promise<void> => {
    if (!checkoutId) {
      return
    }

    const processCheckoutSession = (checkoutSession: CheckoutSession): CheckoutSession => ({
      ...checkoutSession,
      successUrl: checkoutSession.successUrl.replace('{CHECKOUT_SESSION_ID}', checkoutSession.id),
      cancelUrl: checkoutSession.cancelUrl?.replace('{CHECKOUT_SESSION_ID}', checkoutSession.id)
    })

    const c = processCheckoutSession(await Radom.getCheckoutSession(checkoutId))
    const p = await Radom.getSellerProfile(c.organizationId)

    // Which travel rule fields have already been collected?
    setTravelRuleFieldsCollected({
      countryOfResidenceCollected: c.travelRuleFields.countryOfResidenceCollected,
      dateOfBirthCollected: c.travelRuleFields.dateOfBirthCollected,
      emailAddressCollected: c.travelRuleFields.emailAddressCollected,
      nameCollected: c.travelRuleFields.nameCollected,
      companyNameCollected: c.travelRuleFields.companyNameCollected,
      accountType: c.travelRuleFields.accountType,
      addressLine1Collected: c.travelRuleFields.addressLine1Collected,
      addressLine2Collected: c.travelRuleFields.addressLine2Collected,
      townOrCityCollected: c.travelRuleFields.townOrCityCollected,
      postalCodeCollected: c.travelRuleFields.postalCodeCollected,
      stateOrProvinceCollected: c.travelRuleFields.stateOrProvinceCollected
    })

    setSellerProfile(p)
    const cData = {
      ...c,
      sellerLogoUrl: '',
      sellerName: c.sellerAddress,
      products: c.products?.map(p => { return { product: p, quantity: 1 } }) ?? [],
      lineItems: c.items
    }
    setCheckoutData(cData as any)

    if ((c.metadata ?? []).filter((m: any) => m.key === 'locale').length > 0) {
      setLanguage((c.metadata?.find((m: any) => m.key === 'locale')?.value as Language) ?? 'en')
    } else {
      setLanguage('en')
    }

    if (c.gateway.openBanking) {
      setIsOpenbank(true)
      setOpenBankingCheckoutUrl(c.gateway.openBanking?.openBankingCheckoutUrl ?? '')
      if (!c.gateway.openBanking?.destinationCryptoAddress) {
        setRenderOpenBankingState(OpenBankingRenderStates.billing)
      }
    }

    setCustomizations({
      leftPanelColor: c.customizations?.leftPanelColor ?? RADOM_COLORS.GRAY_MED,
      primaryButtonColor: c.customizations?.primaryButtonColor ?? RADOM_COLORS.ORANGE,
      slantedEdge: c.customizations?.slantedEdge ?? false,
      allowDiscountCodes: c.customizations?.allowDiscountCodes ?? false,
      displayQrCode: c.customizations?.displayQrCode ?? true
    })

    setTextColor(
      pickTextColorBasedOnBgColor(
        cData.customizations?.leftPanelColor ?? RADOM_COLORS.GRAY_MED,
        'rgba(255, 255, 255, 0.45)',
        'rgba(0, 0, 0, 0.45)'
      )
    )

    const products: ProductWithQuantity[] = []
    if (c.products) {
      c.products.forEach(p => {
        products.push({
          product: {
            ...p,
            chargingIntervalSeconds: p.chargingIntervalSeconds || 0
          },
          quantity: 1
        })
      })
    }
    if (c.items) {
      c.items.forEach(lineItem => {
        const lineProduct = {
          id: '',
          name: lineItem.name,
          description: '',
          chargingIntervalSeconds: lineItem.chargingIntervalSeconds || 0,
          price: lineItem.price,
          currency: lineItem.currency ?? DEFAULT_FIAT_CURRENCY,
          createdAt: new Date(),
          imageUrl: lineItem.imageUrl,
          updatedAt: new Date(),
          sellerAddress: checkoutData?.sellerAddress ?? ''
        }
        products.push({
          product: lineProduct as any,
          quantity: 1
        })
      })
    }
    setLines(products)

    if (c.expiresAt && (new Date(c.expiresAt) < new Date())) {
      setIsExpired(true)
      if (c.cancelUrl && !c.gateway.openBanking) {
        setTimeout(() => { window.location.href = c.cancelUrl ?? '' }, 2000)
      }
    }

    if (c.sessionStatus === 'cancelled') {
      setIsCancelled(true)
      if (c.cancelUrl && !c.gateway.openBanking) {
        setTimeout(() => { window.location.href = c.cancelUrl ?? '' }, 2000)
      }
    }

    if (c.sessionStatus === 'success' || c.sessionStatus === 'refunded') {
      setIsComplete(true)
      if (c.successUrl && !c.gateway.openBanking) {
        setTimeout(() => { window.location.href = c.successUrl ?? '' }, 3000)
      } else if (c.gateway.openBanking) {
        if (c.successUrl && c.gateway.openBanking.destinationCryptoAddress === null) {
          setTimeout(() => { window.location.href = c.successUrl ?? '' }, 3000)
        } else {
          const checkPaymentInterval = setInterval(async () => {
            await Radom.getCheckoutSession(checkoutId ?? '').then(res => {
              if (res.gateway.openBanking?.instantPaymentStatus === true) {
                clearInterval(checkPaymentInterval)
              }
            })
          }, 3000)
        }
      }
    }

    if (c.total && c.total > 0) {
      setIsSumPurchase(true)
      setTotalSum(c.total)
      setCurrency(c.currency ?? DEFAULT_FIAT_CURRENCY)
    } else {
      let currency = DEFAULT_FIAT_CURRENCY
      if (subscriptionPurchases().length > 0) {
        currency = subscriptionPurchases()[0]?.product.currency ?? DEFAULT_FIAT_CURRENCY
      } else if (cData.products && cData.products.length > 0) {
        currency = cData.products?.[0]?.product?.currency ?? DEFAULT_FIAT_CURRENCY
      }
      setCurrency(currency)
      const productCurrencies = [
        ...cData.items?.map(i => i.currency ?? DEFAULT_FIAT_CURRENCY),
        ...cData.products?.map(p => p.product.currency)
      ]
      Radom.getPriceQuotes(productCurrencies, [currency])
        .then(p => {
          // Get price quotes in terms of first product's currency
          const [total, subscriptionTotal] = getTotalSum(cData as any, p, currency)
          setTotalSum(total)
          setSubscriptionSum(subscriptionTotal)
        })
    }
  }

  const getPriceQuotes = (): void => {
    if (!checkoutData) {
      return
    }

    const productCurrencies = [
      ...checkoutData.lineItems?.map(i => i.currency ?? DEFAULT_FIAT_CURRENCY) ?? [DEFAULT_FIAT_CURRENCY],
      ...checkoutData.products?.map(p => p.product.currency) ?? [DEFAULT_FIAT_CURRENCY]
    ]
    Radom.getPriceQuotes(productCurrencies, [currency])
      .then(p => {
        // Get price quotes in terms of first product's currency
        const [total, subscriptionTotal] = getTotalSum(checkoutData, p, currency)
        setTotalSum(total)
        setSubscriptionSum(subscriptionTotal)
      })
  }

  const onCheckout = async ({
    managed,
    completeTokenDelegate,
    subscriptionOptions
  }: CheckoutParams): Promise<void> => {
    if (managed) {
      if (!checkoutData) throw new Error('Something went wrong')
      if (!checkoutId) throw new Error('Something went wrong')

      setIsPreProcessingPayment(true)
      const { selectedMethod: method } = managed

      let paymentMethod: PaymentMethod = {
        managed: {
          method: {
            network: method.tokenInfo?.networkName ?? method.name,
            token: method.tokenInfo?.tokenAddress ?? undefined
          }
        }
      }

      if (subscriptionOptions) {
        if (subscriptionOptions.autopay) {
          if (!method.tokenInfo) {
            setErrorMessage('Cannot set up an autopay subscription without an EVM based token')
            return
          }

          paymentMethod = {
            managedTokenDelegate: {
              buyerAddress: subscriptionOptions.autopay.address,
              network: method.tokenInfo.networkName,
              token: method.tokenInfo.tokenAddress
            }
          }
        }

        if (subscriptionOptions.emailInvoice) {
          if (!subscriptionOptions.emailInvoice.emailAddress) {
            setErrorMessage('Cannot set up an email invoice subscription without an email address')
            return
          }
        }
      }

      try {
        const res = await Radom.createCheckoutSessionOrder({
          checkoutSessionId: checkoutId,
          paymentMethod,
          orderData: [],
          orderProductData: [],
          emailAddress: subscriptionOptions?.emailInvoice?.emailAddress.toLowerCase(),
          discountCode
        })

        setDiscountLocked(true)

        if (res.paymentMethodDetails.managed) {
          setManagedPaymentDetails(res.paymentMethodDetails.managed)
        } else if (res.paymentMethodDetails.managedTokenDelegate) {
          setTokenDelegatePaymentDetails(res.paymentMethodDetails.managedTokenDelegate)
        } else {
          throw new Error('Unexpected empty managed payment method details')
        }

        const checkPaymentInterval = setInterval(() => {
          if (!res.paymentMethodDetails.managed) {
            return
          }

          Radom.getManagedPaymentStatus(res.paymentMethodDetails.managed?.paymentId).then(res => {
            setPaymentStatus(res)
            if (res.status === 'paymentConfirmed') {
              if (checkoutData.successUrl && !posMode) {
                setTimeout(() => {
                  if (checkoutData.successUrl) {
                    window.location.href = checkoutData.successUrl
                  }
                }, 3000)
              }
              setIsSuccess(true)
              clearInterval(checkPaymentInterval)

              setSuccessElement(
                <div style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  gap: 10
                }}>
                  <CheckmarkWrapper>
                    <Checkmark width={30} height={30} animationDelay='0.5s' animationSpeed='0.75s' />
                  </CheckmarkWrapper>
                  <span style={{ fontSize: 18, color: RADOM_COLORS.BLACK }}>Payment successful</span>
                </div>
              )
            }
          }).catch(() => { })
        }, 1000)
      } catch (err) {
        console.error(err)
        if (err.message === 'Checkout session is complete') {
          setIsLoading(false)
          setIsSuccess(true)
          setSuccessElement(
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              gap: 10
            }}>
              <CheckmarkWrapper>
                <Checkmark width={30} height={30} animationDelay='0.5s' animationSpeed='0.75s' />
              </CheckmarkWrapper>
              <span style={{ fontSize: 18, color: RADOM_COLORS.BLACK }}>Payment successful</span>
            </div>
          )
        } else {
          setErrorMessage(err.reason || err.message)
          setDiscountLocked(false)
        }
      }

      setIsPreProcessingPayment(false)
    }

    if (completeTokenDelegate) {
      const { paymentId, signature } = completeTokenDelegate

      setIsPreProcessingPayment(true)
      try {
        await Radom.completeTokenDelegatePayment(paymentId, signature)
        const checkPaymentInterval = setInterval(() => {
          Radom.getManagedTokenPaymentStatus(paymentId).then(res => {
            setPaymentStatus(res)

            if (res.status === 'paymentConfirmed') {
              setIsPreProcessingPayment(false)
              setIsSuccess(true)
              clearInterval(checkPaymentInterval)

              setSuccessElement(
                <div style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 10
                }}>
                  <CheckmarkWrapper>
                    <Checkmark width={30} height={30} animationDelay='0.5s' animationSpeed='0.75s' />
                  </CheckmarkWrapper>
                  <span style={{ fontSize: 18, color: RADOM_COLORS.BLACK }}>Payment successful</span>
                </div>
              )

              setTimeout(() => {
                if (checkoutData?.successUrl) {
                  window.location.href = checkoutData.successUrl
                }
              }, 3000)
            }

            if (res.status === 'failed') {
              clearInterval(checkPaymentInterval)
              setIsPreProcessingPayment(false)
              setTokenDelegatePaymentDetails(undefined)
              setErrorMessage('Transaction failed, please try again.')
            }
          }).catch(() => { })
        }, 1000)
      } catch (err) {
        setErrorMessage(err.reason || err.message)
        setIsPreProcessingPayment(false)
      }
    }
  }

  const onCancel = (): void => {
    if (checkoutData?.cancelUrl) {
      window.location.href = checkoutData.cancelUrl
    }
  }

  const subscriptionPurchases = (): ProductWithQuantity[] => {
    return lines.filter(l => l.product.chargingIntervalSeconds > 0)
  }

  const redeemDiscountCode = async (discountCode: string): Promise<void> => {
    if (!checkoutId) {
      return
    }

    setDiscountResult({ success: false, element: <Spinner /> })

    Radom.redeemDiscountCode({ checkoutSessionId: checkoutId, code: discountCode }).then((discount) => {
      setDiscount(discount)
      setDiscountResult({
        success: true,
        element: <div style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 14 }}>
          <span>Discount applied!</span>
          <span style={{
            padding: 5,
            backgroundColor: RADOM_COLORS.SUCCESS,
            color: RADOM_COLORS.SUCCESS_LIGHT,
            borderRadius: 5,
            fontWeight: 600,
            fontSize: 12
          }}>
            {
              discount.model.modelType === 'percentage'
                ? new Intl.NumberFormat('en-US', {
                  style: 'percent',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }).format(discount.model.amountOff / 100)
                : formatCurrency(currency, discount.model.amountOff)
            } OFF
          </span>
        </div>
      })
    }).catch(_ => {
      setDiscountResult({
        success: false,
        element: <span style={{ opacity: 0.5, fontSize: 12 }}>Invalid discount code</span>
      })
    })
  }

  const [renderOpenBankingState, setRenderOpenBankingState] =
    useState<OpenBankingRenderStates>(OpenBankingRenderStates.onramp)

  useEffect(() => {
    document.title = 'Radom - Checkout'
    getCheckoutData()
      .finally(() => setIsLoading(false))
  }, [])

  useEffect(() => {
    const discountCode = searchParams.get('discountCode')
    if (discountCode) {
      setDiscountCode(discountCode)
      redeemDiscountCode(discountCode)
    }
  }, [searchParams])

  useEffect(() => {
    if (discountResult?.success) {
      setShowDiscountCodeInput(true)
    }
  }, [discountResult])

  if (posMode) {
    return <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'white',
      zIndex: 100,
      display: 'flex',
      flexDirection: 'column',
      padding: '30px',
      alignItems: 'center',
      justifyContent: 'space-between',
      overflow: 'hidden'
    }}>

      <span style={{ fontWeight: 200 }}>
        {formatCurrency(currency, totalSum)}
      </span>

      {
        !isComplete && checkoutData &&
        <div style={{
          maxWidth: '600px',
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          margin: 'auto'
        }}>
          <PayWidget
            currency={currency}
            checkoutData={checkoutData}
            isPreProcessingPayment={isPreProcessingPayment}
            isSuccess={isSuccess}
            onCheckout={onCheckout}
            managedPaymentDetails={managedPaymentDetails}
            onQuoteUpdate={() => getPriceQuotes()}
            errorMessage={errorMessage}
            successElement={successElement}
            paymentStatus={paymentStatus}
            posMode
            travelRuleFieldsCollected={travelRuleFieldsCollected}
          />
        </div>
      }

    </div>
  }

  if (isOpenbank) {
    return <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: 'auto',
      minHeight: '100vh',
      fontSize: 15,
      background: '#d9dddf'
    }}>
      <div style={{
        position: 'relative',
        padding: openbankPayStatus !== PaymentStatus.WAITING ? '40px 30px' : 0,
        background: 'white',
        minHeight: openbankPayStatus !== PaymentStatus.WAITING ? 680 : '100%%',
        // minHeight: 650,
        transition: '0.2s ease height',
        width: '95vw',
        maxWidth: '500px',
        borderRadius: 15,
        zIndex: 2,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: 30,
        marginTop: 10,
        marginBottom: 10
      }}>
        {
          openbankPayStatus === PaymentStatus.SUCCESS &&
          <div style={{
            display: 'flex',
            position: 'relative',
            margin: '10px 20px',
            background: 'white',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 600
          }}>
            {
              checkoutData?.gateway.openBanking?.destinationCryptoAddress === null && <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexDirection: 'column'
              }}>
                <CheckmarkWrapper style={{ background: RADOM_COLORS.NEW_BLUE, width: 45, height: 45 }}>
                  <Checkmark animationDelay='0.5s' animationSpeed='0.75s' stroke={'white'} />
                </CheckmarkWrapper>
                <span style={{ fontSize: 18, color: RADOM_COLORS.BLACK }}>Payment successful</span>
              </div>
            }

            {
              checkoutData?.gateway.openBanking?.destinationCryptoAddress && (
                <div style={{
                  fontSize: 16
                }}>

                  {/* Title */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    transform: 'translateY(-80px)',
                    alignItems: 'center',
                    marginBottom: 5,
                    width: '100%',
                    fontWeight: 600,
                    fontSize: 26,
                    color: RADOM_COLORS.GRAY4
                  }}>
                    <USDCLogo
                      style={{ width: 48, height: 48, marginBottom: 12 }}
                    />
                    <div>
                        Buying
                      <span style={{
                        color: 'black',
                        fontWeight: 600,
                        marginLeft: 5
                      }}> {Number(checkoutData.gateway.openBanking.onrampAmount).toFixed(2)}  USDC</span>
                    </div>
                    <div>
                        for {formatCurrency(checkoutData.currency ?? '', totalSum)}
                    </div>
                  </div>

                  {/* Step Progress */}
                  <ul style={{
                    position: 'relative',
                    paddingLeft: 45,
                    listStyle: 'none',
                    margin: 0,
                    transform: 'translateY(-20px)'
                  }}>
                    {/* 竖线 */}
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 15,
                        width: 0,
                        height: '95%',
                        borderLeft: '2px solid #CCC',
                        zIndex: 0
                      }}
                    />

                    {/* Step 1 - done */}
                    <li style={{ position: 'relative', paddingBottom: 20, zIndex: 1 }}>
                      <span
                        style={{
                          position: 'absolute',
                          left: -37,
                          top: 0,
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          border: '2px solid green',
                          background: 'green',
                          color: '#fff',
                          fontSize: 10,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
            ✔
                      </span>
                      <strong>Transaction created</strong>
                      <div style={{ color: '#6B6C7E', fontSize: 14 }}>{
                        checkoutData.createdAt ? new Date(checkoutData.createdAt).toLocaleString() : ''}</div>
                    </li>

                    {/* Step 2 - done */}
                    <li style={{ position: 'relative', paddingBottom: 20, zIndex: 1 }}>
                      <span
                        style={{
                          position: 'absolute',
                          left: -37,
                          top: 0,
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          border: '2px solid green',
                          background: 'green',
                          color: '#fff',
                          fontSize: 10,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
            ✔
                      </span>
                      <strong

                      >Verification successful</strong>
                      <div style={{ color: '#6B6C7E', fontSize: 14 }}>
                        {checkoutData.updatedAt ? new Date(checkoutData.updatedAt).toLocaleString() : ''}</div>
                    </li>

                    {/* Step 3 - done */}
                    <li style={{ position: 'relative', paddingBottom: 20, zIndex: 1 }}>
                      <span
                        style={{
                          position: 'absolute',
                          left: -37,
                          top: 0,
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          border: '2px solid green',
                          background: 'green',
                          color: '#fff',
                          fontSize: 10,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
            ✔
                      </span>
                      <strong

                      >Payment confirmed</strong>
                      <div style={{ color: '#6B6C7E', fontSize: 14 }}>{
                        checkoutData.updatedAt ? new Date(checkoutData.updatedAt).toLocaleString() : ''
                      }</div>
                    </li>

                    {/* Step 4 - current */}
                    <li style={{ position: 'relative', paddingBottom: 20, zIndex: 1 }}>
                      <span
                        style={{
                          position: 'absolute',
                          left: -37,
                          top: 0,
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          border: checkoutData?.gateway.openBanking?.instantPaymentStatus
                            ? '2px solid green'
                            : '2px solid green',
                          background: checkoutData?.gateway.openBanking?.instantPaymentStatus ? 'green' : '#fff',
                          color: checkoutData?.gateway.openBanking?.instantPaymentStatus ? '#fff' : 'green',
                          fontSize: 10,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {checkoutData?.gateway.openBanking?.instantPaymentStatus
                          ? '✔'
                          : <div style={{
                            width: 10,
                            height: 10,
                            border: '2px solid green',
                            borderTop: '2px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }} />}
                      </span>
                      <strong>Final checks in progress</strong>
                      <div style={{ color: '#6B6C7E', fontSize: 14 }}>
                        {new Date().toLocaleString()}
                      </div>
                      <div style={{ fontSize: 14, color: '#6B6C7E' }}>
                        {"We're completing final checks to keep your transaction secure and compliant with local regulations."}                      </div>
                    </li>

                    <style>
                      {`
                    @keyframes spin {
                      from { transform: rotate(0deg); }
                      to { transform: rotate(360deg); }
                    }
                    `}
                    </style>

                    {/* Step 5 - todo */}
                    {checkoutData?.gateway.openBanking?.instantPaymentStatus
                      ? (
                        <li style={{ position: 'relative', zIndex: 1 }}>
                          <span
                            style={{
                              position: 'absolute',
                              left: -37,
                              top: 0,
                              width: 14,
                              height: 14,
                              borderRadius: '50%',
                              border: '2px solid green',
                              background: 'green',
                              color: '#fff',
                              fontSize: 10,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                          ✔
                          </span>
                          <strong>Crypto delivered</strong>
                        </li>
                      )
                      : (
                        <li style={{ position: 'relative', zIndex: 1 }}>
                          <span
                            style={{
                              position: 'absolute',
                              left: -37,
                              top: 0,
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              border: '2px solid #CCC',
                              background: '#fff'
                            }}
                          />
                          <strong style={{ color: '#6B6C7E' }}>Crypto delivery</strong>
                        </li>
                      )}
                  </ul>
                </div>
              )
            }

            <div style={{
              bottom: 0,
              position: 'absolute',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 10,
              marginBottom: 30
            }}>
              <a
                href='https://www.radom.com/crypto-presales'
                target='_blank'
                title='Crypto Presale Powered by Radom'
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  cursor: 'pointer',
                  userSelect: 'none',
                  textDecoration: 'none',
                  color: RADOM_COLORS.GRAY_DARKEST
                }} rel="noreferrer"
              >
                <span style={{ display: 'flex', alignItems: 'center', fontSize: 14, fontWeight: 100 }}>
                  Powered by
                  <RadomLogo
                    width={60}
                    height={14}
                    fill={RADOM_COLORS.GRAY_DARKEST}
                  />
                </span>
                <span style={{ position: 'absolute', width: '1px', height: '1px', margin: '-1px', padding: 0, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', border: 0 }}>
                  Radom
                </span>
              </a>
            </div>
          </div>
        }

        {
          openbankPayStatus === PaymentStatus.WAITING &&
          <IvyCheckout
            data-hj-allow-iframe
            checkoutUrl={openBankingCheckoutUrl}
            displayOptions={{
              type: 'embedded'
            }}
            onSuccess={() => {
              setOpenbankPayStatus(PaymentStatus.KYC_PENDING_VIEW)
              if (checkoutData?.gateway.openBanking?.destinationCryptoAddress === null || checkoutData?.gateway.openBanking?.destinationCryptoAddress === '') {
                setTimeout(() => {
                  setOpenbankPayStatus(PaymentStatus.SUCCESS)
                }, 3000)
                setTimeout(() => {
                  window.location.replace(checkoutData?.successUrl ?? '')
                }, 1500)
              } else {
                setOpenbankPayStatus(PaymentStatus.SUCCESS)
                const checkPaymentInterval = setInterval(async () => {
                  await Radom.getCheckoutSession(checkoutId ?? '').then(res => {
                    if (res.gateway.openBanking?.instantPaymentStatus === true) {
                      setOpenbankPayStatus(PaymentStatus.SUCCESS)
                      clearInterval(checkPaymentInterval)
                      window.location.reload()
                    }
                  })
                }, 3000)
              }
            }}
            onCancel={() => {
              window.location.replace(checkoutData?.cancelUrl ?? '')
            }}
          />
        }

        {
          openbankPayStatus === PaymentStatus.INITIAL &&
          <RenderOpenBankingInitial
            renderState={renderOpenBankingState}
            setRenderState={setRenderOpenBankingState}
            checkoutData={checkoutData}
            totalSum={totalSum}
            currency={currency}
            isPreProcessingPayment={isPreProcessingPayment}
            onrampAmount={checkoutData?.gateway.openBanking?.onrampAmount ?? 0}
            randomAddress={randomAddress}
            userKycData={userKycData}
            setUserKycData={setUserKycData}
            addressSuggestionList={addressSuggestionList}
            setAddressSuggestionList={setAddressSuggestionList}
            allCountries={allCountries}
            debouncedFetch={debouncedFetch}
            checkMarkOpenbanking={checkMarkOpenbanking}
            setCheckMarkOpenBanking={setCheckMarkOpenBanking}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            setOpenbankPayStatus={setOpenbankPayStatus}
            setOpenBankingCheckoutUrl={setOpenBankingCheckoutUrl}
            openBankingCheckoutUrl={openBankingCheckoutUrl}
            setOpenBankingTotalUsdRate={() => {}}
          />
        }

        {
          openbankPayStatus === PaymentStatus.KYC &&
          <div>
            {
              isLoading &&
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100vw',
                  height: '100vh',
                  backgroundColor: 'rgba(0, 0, 0, 0.15)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 9999
                }}
              >
                <CheckmarkWrapper style={{ background: RADOM_COLORS.NEW_BLUE, width: 50, height: 50 }}>
                  <PendingSpinner animationDelay='0.5s' animationSpeed='0.75s' stroke={'white'} />
                </CheckmarkWrapper>
              </div>
            }
            <KycView
              kycTips={'KYC verification will apply if you deposit more than 1K EUR per single transaction or in total for 24 hours'}
              errorMessage={errorMessage}
              onBack={() => {
                setOpenbankPayStatus(PaymentStatus.INITIAL)
                if (openBankingIntervalRef.current) {
                  clearInterval(openBankingIntervalRef.current)
                }
              }}
              setOpenbankPayStatus={setOpenbankPayStatus}
              onSubmit={() => {
                setIsLoading(true)
                setOpenbankPayStatus(PaymentStatus.KYC_PENDING_VIEW)
                openBankingIntervalRef.current = setInterval(() => {
                  Radom.getCheckoutSession(checkoutId ?? '').then((res) => {
                    const checkoutUrl = res.gateway.openBanking?.openBankingCheckoutUrl
                    if (checkoutUrl && checkoutUrl !== '') {
                      if (checkoutUrl.includes('https')) {
                        setOpenBankingCheckoutUrl(checkoutUrl)
                        setOpenbankPayStatus(PaymentStatus.WAITING)
                        if (openBankingIntervalRef.current) {
                          clearInterval(openBankingIntervalRef.current)
                        }
                      } else {
                        setErrorMessage(checkoutUrl)
                        setOpenbankPayStatus(PaymentStatus.ERROR)
                      }
                    }
                  })
                }, 2000)
              }}
              onChange={async (value) => {
                setIsLoading(true)
                if (value.kycType === 2) {
                  if (value.documentType === 'passport') {
                    setUserKycData({
                      ...userKycData,
                      passportFile: value.imageFile
                    })
                    await Radom.updateCustomerKycData(
                      checkoutId ?? '',
                      {
                        name: userKycData.name,
                        phone: userKycData.phoneNumber,
                        address: userKycData.address,
                        dateOfBirth: new Date(userKycData.dateOfBirth).toLocaleDateString('en-CA'),
                        residenceCountry: userKycData.countryOfResidence,
                        passportFile: value.imageFile
                      }
                    ).finally(() => {
                      setIsLoading(false)
                    })
                  } else if (value.documentType === 'id_card') {
                    setUserKycData({
                      ...userKycData,
                      idCardFile: value.imageFile
                    })
                    await Radom.updateCustomerKycData(
                      checkoutId ?? '',
                      {
                        name: userKycData.name,
                        phone: userKycData.phoneNumber,
                        address: userKycData.address,
                        dateOfBirth: new Date(userKycData.dateOfBirth).toLocaleDateString('en-CA'),
                        residenceCountry: userKycData.countryOfResidence,
                        idCardFile: value.imageFile
                      }
                    ).finally(() => {
                      setIsLoading(false)
                    })
                  }
                } else if (value.kycType === 3) {
                  await Radom.updateCustomerKycData(
                    checkoutId ?? '',
                    {
                      name: userKycData.name,
                      phone: userKycData.phoneNumber,
                      address: userKycData.address,
                      dateOfBirth: new Date(userKycData.dateOfBirth).toLocaleDateString('en-CA'),
                      residenceCountry: userKycData.countryOfResidence,
                      selfieFile: value.imageFile
                    }
                  ).finally(() => {
                    setIsLoading(false)
                  })
                  setUserKycData({
                    ...userKycData,
                    selfieFile: value.imageFile
                  })
                }
              }}
            />
          </div>
        }

        {
          openbankPayStatus === PaymentStatus.Expired &&
          <div style={{
            display: 'flex',
            position: 'relative',
            margin: '10px 20px',
            background: '#f5f6f6',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 600
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexDirection: 'column'
            }}>
              <CheckmarkWrapper style={{ background: RADOM_COLORS.ERROR, width: 45, height: 45 }}>
                <Crossmark animationDelay='0.5s' animationSpeed='0.75s' stroke={'white'} />
              </CheckmarkWrapper>
              <span style={{ fontSize: 18, color: RADOM_COLORS.BLACK }}>Payment Expired</span>
            </div>

            <div style={{
              bottom: 0,
              position: 'absolute',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 10,
              marginBottom: 30
            }}>
              <a
                href='https://www.radom.com/crypto-presales'
                target='_blank'
                title='Crypto Presale Powered by Radom'
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  cursor: 'pointer',
                  userSelect: 'none',
                  textDecoration: 'none',
                  color: RADOM_COLORS.GRAY_DARKEST
                }} rel="noreferrer"
              >
                <span style={{ display: 'flex', alignItems: 'center', fontSize: 14, fontWeight: 100 }}>
                  Powered by
                  <RadomLogo
                    width={60}
                    height={14}
                    fill={RADOM_COLORS.GRAY_DARKEST}
                  />
                </span>
                <span style={{ position: 'absolute', width: '1px', height: '1px', margin: '-1px', padding: 0, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', border: 0 }}>
                  Radom
                </span>
              </a>
            </div>
          </div>
        }

        {
          openbankPayStatus === PaymentStatus.ERROR &&
          <div style={{
            display: 'flex',
            position: 'relative',
            margin: '10px 20px',
            background: '#f5f6f6',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 600
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexDirection: 'column'
            }}>
              <CheckmarkWrapper style={{ background: RADOM_COLORS.ERROR, width: 45, height: 45 }}>
                <Crossmark animationDelay='0.2s' animationSpeed='0.15s' stroke={'white'} />
              </CheckmarkWrapper>
              <span style={{ fontSize: 18, color: RADOM_COLORS.BLACK }}>KYC Verified False</span>

              {
                errorMessage && errorMessage.length > 0 &&
              <div style={{
                backgroundColor: RADOM_COLORS.ERROR,
                padding: 15,
                borderRadius: 5,
                marginTop: 20,
                // marginBottom: 20,
                color: 'white',
                overflowWrap: 'break-word'
              }}>
                {errorMessage}
              </div>
              }
            </div>

            <div style={{
              bottom: 0,
              position: 'absolute',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 10,
              marginBottom: 30
            }}>
              <a
                href='https://www.radom.com/crypto-presales'
                target='_blank'
                title='Crypto Presale Powered by Radom'
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  cursor: 'pointer',
                  userSelect: 'none',
                  textDecoration: 'none',
                  color: RADOM_COLORS.GRAY_DARKEST
                }} rel="noreferrer"
              >
                <span style={{ display: 'flex', alignItems: 'center', fontSize: 14, fontWeight: 100 }}>
                  Powered by
                  <RadomLogo
                    width={60}
                    height={14}
                    fill={RADOM_COLORS.GRAY_DARKEST}
                  />
                </span>
                <span style={{ position: 'absolute', width: '1px', height: '1px', margin: '-1px', padding: 0, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', border: 0 }}>
                  Radom
                </span>
              </a>
            </div>
          </div>
        }

        {
          openbankPayStatus === PaymentStatus.KYC_PENDING_VIEW &&
          <div style={{
            display: 'flex',
            position: 'relative',
            margin: '10px 20px',
            background: 'white',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 600
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexDirection: 'column'
            }}>
              <CheckmarkWrapper style={{ background: RADOM_COLORS.NEW_BLUE, width: 60, height: 60 }}>
                <PendingSpinner animationDelay='0.5s' animationSpeed='0.75s' stroke={'white'} />
              </CheckmarkWrapper>

            </div>

            <div style={{
              bottom: 0,
              position: 'absolute',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 10,
              marginBottom: 30
            }}>
              <a
                href='https://www.radom.com/crypto-presales'
                target='_blank'
                title='Crypto Presale Powered by Radom'
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  cursor: 'pointer',
                  userSelect: 'none',
                  textDecoration: 'none',
                  color: RADOM_COLORS.GRAY_DARKEST
                }} rel="noreferrer"
              >
                <span style={{ display: 'flex', alignItems: 'center', fontSize: 14, fontWeight: 100 }}>
                  Powered by
                  <RadomLogo
                    width={60}
                    height={14}
                    fill={RADOM_COLORS.GRAY_DARKEST}
                  />
                </span>
                <span style={{ position: 'absolute', width: '1px', height: '1px', margin: '-1px', padding: 0, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', border: 0 }}>
                  Radom
                </span>
              </a>
            </div>
          </div>
        }

      </div>

    </div >
  }

  return <Container>
    {!isLoading && !checkoutData && <div>Checkout id not found</div>}
    {
      checkoutData &&
      <>
        <div style={{ width: '100%', height: '100%' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', height: '100%' }}>
            <LeftSection
              slantedEdge={checkoutData.customizations?.slantedEdge}
              leftPanelColor={checkoutData.customizations?.leftPanelColor ?? RADOM_COLORS.GRAY_MED}>
              <div style={{
                padding: '20px 10px',
                fontSize: 14,
                display: 'flex',
                width: '100%',
                alignItems: 'center'
              }}>
                {
                  checkoutData.cancelUrl &&
                  <IconButton onClick={onCancel} style={{
                    padding: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    fontSize: 14
                  }}>
                    <Arrow stroke={textColor} style={{
                      width: 20,
                      height: 15,
                      transform: 'rotate(180deg)'
                    }} />
                  </IconButton>
                }
                <div style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 8,
                  alignItems: 'center',
                  color: textColor
                }}>
                  {
                    sellerProfile?.logoUrl &&
                    <img src={sellerProfile?.logoUrl} style={{ width: 'auto', height: 25, pointerEvents: 'none' }} />
                  }
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
                    {sellerProfile?.name
                      ? <span>{sellerProfile.name}</span>
                      : <span>{shortAddress(checkoutData.sellerAddress, 5)}</span>
                    }
                  </div>
                </div>

              </div>
              <LeftSectionInner
                isSumPurchase={
                  checkoutData.organizationId === 'f58cf6b3-b87d-4893-b859-cddc85ac0abf' ||
                  isSumPurchase || (lines.length === 1 && lines[0].product.chargingIntervalSeconds > 0)
                }>

                {
                  checkoutData.organizationId === 'f58cf6b3-b87d-4893-b859-cddc85ac0abf' &&
                  subscriptionPurchases().length === 0 &&
                  checkoutData.products &&
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                    maxWidth: '400px',
                    alignSelf: 'center',
                    justifyContent: 'center'
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {checkoutData.products.length > 1 &&
                        <span style={{ fontSize: 28 }}>
                          Pay {checkoutData.sellerName}
                        </span>
                      }
                      {checkoutData.products.length === 1 &&
                        <span style={{ fontSize: 28 }}>{checkoutData.products[0]?.product.name}</span>}
                      {checkoutData.products?.length === 0 && <span style={{ fontSize: 28 }}>Product name</span>}
                      {
                        checkoutData.products?.length === 1 && checkoutData.products[0]?.product.description &&
                        <span style={{ fontSize: 14, opacity: 0.5, whiteSpace: 'pre' }}>
                          {checkoutData.products[0]?.product.description}
                        </span>
                      }
                    </div>
                    {
                      isLoading && <Spinner />
                    }
                    {
                      !isLoading && <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', fontSize: 54, fontWeight: 100 }}>
                        {formatCurrency(currency, totalSum)}
                      </div>
                    }
                    {
                      checkoutData.products?.length === 1 && checkoutData.products[0]?.product.imageUrl &&
                      <img style={{
                        marginTop: 10,
                        maxWidth: 200,
                        maxHeight: 200
                      }} src={checkoutData.products[0]?.product.imageUrl} />
                    }
                  </div>
                }

                {
                  checkoutData.organizationId !== 'f58cf6b3-b87d-4893-b859-cddc85ac0abf' &&
                  <div style={{ fontSize: 40, wordWrap: 'break-word' }}>
                    <div style={{ fontSize: 20 }}>
                      {
                        subscriptionPurchases().length === 0 &&
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          overflowWrap: 'anywhere',
                          gap: 5,
                          fontSize: 22
                        }}>
                          <span style={{ opacity: 0.65, fontWeight: 100 }}>Pay</span>
                          <span style={{
                            fontWeight: sellerProfile?.name ? 300 : 100
                          }}>
                            {sellerProfile?.name ? sellerProfile.name : shortAddress(checkoutData.sellerAddress, 8)}
                          </span>
                        </div>
                      }
                      {
                        subscriptionPurchases().length > 0 && <>
                          <span style={{ fontSize: 28, display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: 100, fontSize: 18, opacity: 0.5 }}>Subscribe to</span>
                            {subscriptionPurchases()[0].product.name}
                          </span>
                        </>
                      }
                    </div>
                    <div>
                      {isLoading && <Spinner />}
                      {
                        !isLoading && <div style={{ fontWeight: 200 }}>
                          <span style={{ fontSize: 48 }}>{formatCurrency(currency, totalSum)}</span>
                          {
                            subscriptionPurchases().length > 0 &&
                            subscriptionPurchases().length === checkoutData?.products?.length &&
                            <div style={{ display: 'inline-block', marginLeft: '4px', lineHeight: '14px', fontSize: '14px' }}>
                              <div>per</div>
                              <div>{
                                formatSubscriptionDuration(subscriptionPurchases()[0].product.chargingIntervalSeconds)
                              }</div>
                            </div>
                          }
                        </div>
                      }
                      {
                        subscriptionPurchases().length > 0 &&
                        subscriptionPurchases().length !== checkoutData?.products?.length &&
                        <div style={{ fontSize: 14, color: textColor }}>
                          then {
                            formatCurrency(currency, subscriptionSum)
                          } per {
                            formatSubscriptionDuration(subscriptionPurchases()[0].product.chargingIntervalSeconds)
                          }
                        </div>
                      }
                    </div>
                  </div>
                }
                {
                  checkoutData.organizationId !== 'f58cf6b3-b87d-4893-b859-cddc85ac0abf' &&
                  !isSumPurchase &&
                  <div style={lines.length === 1 && lines[0].product.chargingIntervalSeconds > 0 ? {} : { width: '100%' }}>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 20,
                      width: '100%',
                      maxHeight: 250,
                      overflow: 'scroll',
                      justifyContent: 'space-between'
                    }}>
                      {
                        lines.length === 1 &&
                        lines[0].product.chargingIntervalSeconds > 0 &&
                        lines[0].product.imageUrl &&
                        <img style={{
                          marginTop: 10,
                          maxWidth: 200,
                          maxHeight: 200,
                          borderRadius: 5
                        }} src={lines[0].product.imageUrl} />
                      }
                      {
                        (lines.length > 1 ||
                          (lines.length === 1 && lines[0].product.chargingIntervalSeconds === 0)) &&
                        lines.map((p, i) =>
                          <div key={i} style={{
                            fontSize: 14,
                            display: 'flex',
                            width: '100%',
                            justifyContent: 'space-evenly',
                            alignItems: 'center',
                            gap: 20
                          }}>
                            {
                              p.product.imageUrl &&
                              <img style={{ width: 30, height: 30 }} src={p.product.imageUrl} />
                            }
                            <div style={{ flexGrow: 1 }}>
                              <div style={{ marginBottom: 4 }}>{p.product.name}</div>
                              <div style={{ fontSize: 12, opacity: 0.5 }}>
                                Qty {p.quantity}
                                {/* <QuantityInput disabled={isSuccess || isLoading || isExpired} type="number" min="1" value={p.quantity} onChange={e => handleQuantityChange(e, i)} /> */}
                              </div>
                            </div>
                            <div style={{
                              alignSelf: 'start',
                              textAlign: 'right',
                              display: 'flex',
                              flexDirection: 'column'
                            }}>
                              {
                                isLoading && <Spinner />
                              }
                              {
                                !isLoading && <>
                                  <span>
                                    {formatCurrency(p.product.currency, p.product.price * p.quantity)}
                                    {
                                      p.product.chargingIntervalSeconds > 0 &&
                                      <span style={{ fontSize: '14px', color: textColor }}> / {formatSubscriptionDuration(p.product.chargingIntervalSeconds).toLowerCase()}</span>
                                    }
                                  </span>
                                  {p.quantity > 1 &&
                                    <span style={{ fontSize: '14px', textAlign: 'right', color: textColor }}>
                                      {formatCurrency(p.product.currency, p.product.price)} each
                                    </span>
                                  }
                                </>
                              }
                            </div>
                          </div>
                        )
                      }
                    </div>
                  </div>
                }

                {
                  checkoutData.customizations?.allowDiscountCodes && !showDiscountCodeInput && !discountLocked &&
                  <IconButton
                    onClick={() => { setShowDiscountCodeInput(true) }}
                    style={{
                      marginTop: 10,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      padding: 0,
                      color: pickTextColorBasedOnBgColor(customizations?.leftPanelColor, 'rgba(255, 255, 255, 0.5)', 'rgba(0, 0, 0, 0.5)')
                    }}>
                    <span>Add discount code</span>
                    <Arrow stroke={pickTextColorBasedOnBgColor(customizations?.leftPanelColor, 'rgba(255, 255, 255, 0.5)', 'rgba(0, 0, 0, 0.5)')} height={15} />
                  </IconButton>
                }
                {
                  showDiscountCodeInput &&
                  <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {
                      !discountResult?.success && !discountLocked &&
                      <div style={{
                        borderRadius: 5,
                        border: '1px solid',
                        backgroundColor: pickTextColorBasedOnBgColor(customizations.leftPanelColor, 'rgba(255, 255, 255, 0.1)', 'rgba(255, 255, 255, 0.25)'),
                        color: pickTextColorBasedOnBgColor(customizations.leftPanelColor, 'white', 'black'),
                        borderColor: pickTextColorBasedOnBgColor(customizations.leftPanelColor, 'rgba(255, 255, 255, 0.1)', 'rgba(0, 0, 0, 0.1)'),
                        display: 'flex',
                        alignItems: 'center'
                      }}>

                        <TextInput
                          autoFocus
                          placeholder='Enter code'
                          autoCorrect="off"
                          spellCheck="false"
                          type="text"
                          onChange={(e) => setDiscountCode(e.target.value)}
                          style={{
                            color: pickTextColorBasedOnBgColor(customizations.leftPanelColor, 'white', 'black'),
                            background: 'none',
                            border: 'none',
                            boxShadow: 'none'
                          }}
                        />
                        <SecondaryButton
                          onClick={async () => await redeemDiscountCode(discountCode)}
                          style={{
                            padding: '3px 10px',
                            textTransform: 'uppercase',
                            color: pickTextColorBasedOnBgColor(customizations.leftPanelColor, 'white', 'black'),
                            border: 'none'
                          }}>Apply</SecondaryButton>
                      </div>
                    }
                    <div style={{ display: 'flex', gap: 10 }}>
                      {discountResult?.element}
                      {discountResult?.success && !discountLocked &&
                        <IconButton
                          style={{ padding: 5 }}
                          onClick={() => {
                            setDiscountResult({ success: false, element: <></> })
                            setDiscount(undefined)
                            setShowDiscountCodeInput(false)
                          }}>
                          <Close
                            width={15}
                            height={15}
                            stroke={pickTextColorBasedOnBgColor(customizations.leftPanelColor, 'white', 'black')}
                            style={{ opacity: 0.5 }} />
                        </IconButton>
                      }
                    </div>
                  </div>
                }
              </LeftSectionInner>
              <div style={{
                position: 'relative',
                padding: 30,
                fontSize: '14px',
                alignSelf: 'start'
              }}>
                <a
                  href='https://radom.com'
                  target='_blank'
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 6,
                    cursor: 'pointer',
                    userSelect: 'none',
                    fontSize: 14,
                    whiteSpace: 'nowrap',
                    color: textColor,
                    fontWeight: 100,
                    textDecoration: 'none'
                  }} rel="noreferrer">
                  <span>Checkout by</span>
                  <RadomLogo
                    width={50}
                    fill={textColor}
                  />
                </a>
              </div>
            </LeftSection>

            <RightSection>
              <PayWrapper>
                {
                  isComplete &&
                  <div>
                    <div>Session complete{checkoutData.successUrl && <span>, redirecting...</span>}</div>
                  </div>
                }
                {!isComplete && isExpired && !isCancelled &&
                  <div>
                    <div>Session expired{checkoutData.cancelUrl && <span>, redirecting...</span>}</div>
                  </div>
                }
                {isCancelled &&
                  <div>
                    <div>Session cancelled{checkoutData.cancelUrl && <span>, redirecting...</span>}</div>
                  </div>
                }
                {
                  !isComplete && !isExpired && !isCancelled &&
                  <PayWidget
                    currency={currency}
                    checkoutData={checkoutData}
                    isPreProcessingPayment={isPreProcessingPayment}
                    isSuccess={isSuccess}
                    onCheckout={onCheckout}
                    managedPaymentDetails={managedPaymentDetails}
                    tokenDelegatePaymentDetails={tokenDelegatePaymentDetails}
                    onQuoteUpdate={() => getPriceQuotes()}
                    errorMessage={errorMessage}
                    discount={discount}
                    successElement={successElement}
                    paymentStatus={paymentStatus}
                    travelRuleFieldsCollected={travelRuleFieldsCollected}
                  />
                }
              </PayWrapper>
              <div
                style={{
                  marginTop: 'auto',
                  fontSize: 14,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 30,
                  padding: '40px 0',
                  color: RADOM_COLORS.GRAY_DARKER,
                  cursor: 'pointer'
                }}
              >
                <span onClick={() => window.open('https://radom.com/privacy', '_blank')}>
      Privacy
                </span>
                <span onClick={() => window.open('https://www.radom.com/terms', '_blank')}>
      Terms
                </span>
              </div>
            </RightSection>
          </div>
        </div>
      </>
    }
  </Container>
}

export default HostedCheckout
